import React from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { fadeUpAt } from "../lib/motion";

function CountUp({ value, prefix = "", suffix = "", duration = 1.6 }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const mv = useMotionValue(0);
  const [display, setDisplay] = React.useState(`${prefix}0${suffix}`);

  React.useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        const n = Math.round(v);
        setDisplay(`${prefix}${n.toLocaleString()}${suffix}`);
      },
    });
    return controls.stop;
  }, [inView, value, duration, prefix, suffix, mv]);

  return (
    <span ref={ref} className="tabular-nums">
      {display}
    </span>
  );
}

export default function ProblemSection() {
  const stats = [
    {
      n: 65, prefix: "", suffix: "%",
      label: "of inbound calls to contractors go unanswered.",
    },
    {
      n: 5, prefix: "", suffix: " min",
      label: "the window before a hot lead goes cold.",
    },
    {
      n: 11400, prefix: "$", suffix: "",
      label: "average value of one missed roofing deal.",
    },
  ];

  return (
    <section
      data-testid="problem-section"
      className="bg-[#FAF7F2] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="kicker">The problem</p>
            <h2 className="h-section text-[clamp(2rem,4vw,3.4rem)] mt-5 max-w-md">
              Your phone rings while you're forty feet up.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-3 gap-10">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  {...fadeUpAt(i, 0.08, 0.6)}
                  viewport={{ once: true, margin: "-15% 0px" }}
                  className="border-t border-[#E5E0D8] pt-6"
                  data-testid={`stat-${i}`}
                >
                  <p className="font-sans-tight font-black tracking-tighter text-[clamp(2.6rem,4.5vw,3.6rem)] text-[#0B3D5C] leading-none">
                    <CountUp value={s.n} prefix={s.prefix} suffix={s.suffix} />
                  </p>
                  <p className="font-serif-body text-[#4A4A4A] mt-4 text-[1.02rem] leading-relaxed">
                    {s.label}
                  </p>
                </motion.div>
              ))}
            </div>
            <p className="font-serif-body text-[#1A1A1A] mt-12 text-[1.15rem] leading-relaxed max-w-2xl">
              LeadRiver catches what your office misses. Every lead, every
              estimate, every renewal. Drafted and ready before you climb down.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
